import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScrollText, ShieldCheck, Sparkles, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePublishedPolicy } from "@/lib/consent";

export type LegalDocumentKind = "terms" | "privacy";

interface LegalDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: LegalDocumentKind;
}

interface RenderedSection {
  title: string;
  content?: string;
  intro?: string;
  items?: string[];
  outro?: string;
}

/**
 * Reads the localised i18n sections for either Terms of Service or Privacy
 * Policy and returns a normalised array. Sections support either a single
 * `content` block, or an `intro` + `items` (+ optional `outro`) structure.
 */
function useLocalisedSections(kind: LegalDocumentKind): RenderedSection[] {
  const { t } = useTranslation();
  const root =
    kind === "terms" ? "legal.termsOfService" : "legal.privacyPolicy";

  // Both docs have up to 11 sections; we just probe and skip empties.
  const sections: RenderedSection[] = [];
  for (let i = 1; i <= 11; i += 1) {
    const base = `${root}.section${i}`;
    const titleKey = `${base}.title`;
    const title = t(titleKey, { defaultValue: "" });
    if (!title) continue;

    const content = t(`${base}.content`, { defaultValue: "" });
    const intro = t(`${base}.intro`, { defaultValue: "" });
    const outro = t(`${base}.outro`, { defaultValue: "" });
    const itemsRaw = t(`${base}.items`, {
      returnObjects: true,
      defaultValue: [] as string[],
    }) as unknown;
    const items = Array.isArray(itemsRaw) ? (itemsRaw as string[]) : [];

    sections.push({
      title,
      content: content || undefined,
      intro: intro || undefined,
      items: items.length ? items : undefined,
      outro: outro || undefined,
    });
  }

  return sections;
}

export function LegalDocumentDialog({
  open,
  onOpenChange,
  kind,
}: LegalDocumentDialogProps) {
  const { t } = useTranslation();
  // Prefer the admin-published version stored in the database when available;
  // fall back to the bundled i18n strings otherwise.
  const { policy } = usePublishedPolicy(kind === "terms" ? "terms" : "privacy");
  const sections = useLocalisedSections(kind);

  const Icon = kind === "terms" ? ScrollText : ShieldCheck;
  const eyebrow = t("legal.badge", "Legal");
  const title =
    kind === "terms"
      ? t("legal.termsOfService.title", "Terms of Service")
      : t("legal.privacyPolicy.title", "Privacy Policy");
  const lastUpdated = policy
    ? `${t("legal.lastUpdated")} · v${policy.version}`
    : t("legal.lastUpdated");

  const fullPagePath = kind === "terms" ? "/terms-of-service" : "/privacy-policy";
  const openFullCta =
    kind === "terms"
      ? t("auth.openFullTerms", "Open full Terms of Service")
      : t("auth.openFullPrivacy", "Open full Privacy Policy");

  const body: ReactNode = policy ? (
    <article className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap leading-relaxed">
      {policy.body}
    </article>
  ) : (
    <div className="space-y-7">
      {sections.map((s, idx) => (
        <section key={idx} className="space-y-2.5">
          <h3 className="text-base font-semibold text-foreground">{s.title}</h3>
          {s.content && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {s.content}
            </p>
          )}
          {s.intro && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {s.intro}
            </p>
          )}
          {s.items && (
            <ul className="space-y-1.5 pl-1">
              {s.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-foreground/85"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          )}
          {s.outro && (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {s.outro}
            </p>
          )}
        </section>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl",
            "-translate-x-1/2 -translate-y-1/2 outline-none",
          )}
        >
          <div
            className={cn(
              "card-premium border border-primary/20 rounded-2xl overflow-hidden shadow-glass-hover",
              "transition-opacity duration-200",
              open ? "opacity-100" : "opacity-0",
            )}
          >
            {/* Hero band */}
            <div className="relative h-32 sm:h-40 w-full overflow-hidden bg-gradient-to-br from-primary/25 to-accent/20">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-3xl bg-card/40 backdrop-blur-xl border border-primary/30 flex items-center justify-center shadow-glass">
                  <Icon className="w-10 h-10 text-primary" />
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background to-transparent pointer-events-none" />

              <DialogPrimitive.Close
                className="absolute right-3 top-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-background/70 backdrop-blur-md border glass-border text-foreground/80 hover:text-foreground hover:bg-background/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={t("common.close", "Close")}
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="px-6 sm:px-8 pt-5 pb-2 space-y-2">
              <DialogHeader className="space-y-1.5 text-left">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="text-[0.65rem] font-medium tracking-[0.18em] uppercase text-primary/80">
                    {eyebrow}
                  </span>
                </div>
                <DialogTitle className="font-serif italic text-2xl sm:text-3xl leading-tight">
                  <span className="bg-gradient-primary bg-clip-text text-transparent pb-1 inline-block">
                    {title}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  {lastUpdated}
                </DialogDescription>
              </DialogHeader>
            </div>

            <ScrollArea className="px-6 sm:px-8 h-[55vh] max-h-[55vh] overscroll-contain">
              <div className="pb-6 pr-2">{body}</div>
            </ScrollArea>

            <div className="px-6 sm:px-8 py-4 border-t border-border/60 bg-card/40 backdrop-blur-md">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="w-full sm:w-auto group"
              >
                <Link to={fullPagePath} onClick={() => onOpenChange(false)}>
                  {openFullCta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export default LegalDocumentDialog;
